import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, LinearProgress } from "@mui/material";
import Swal from "sweetalert2";
import {
  getBuyerStatusJob,
  runBuyerStatusJob,
  stopBuyerStatusJob,
} from "../../Actions/ProgramSettingsAction";
import { globalStyle, handleDangerVisible } from "../../global/Utils";
import { formatCount, formatDateTime, formatDuration } from "../Atl/format";

// How often the server is asked how a running update is getting on.
const POLL_INTERVAL_MS = 2000;

const STATUS_STYLES = {
  completed: { label: "Completed", className: "bg-green-100 text-green-700" },
  stopped: { label: "Stopped", className: "bg-amber-100 text-amber-700" },
  failed: { label: "Failed", className: "bg-red-100 text-red-700" },
  running: { label: "Running", className: "bg-blue-100 text-blue-700" },
};

// How the box around the last run looks, by how it ended.
const RESULT_STYLES = {
  completed: { box: "border-green-200 bg-green-50", title: "text-green-800" },
  stopped: { box: "border-amber-200 bg-amber-50", title: "text-amber-900" },
  failed: { box: "border-red-200 bg-red-50", title: "text-red-700" },
};

const ACCOUNT_REASONS = {
  noToken: "No FBR token saved on its company",
  tokenRefused: "FBR refused its token (expired or wrong)",
};

const buyers = (count) => `${formatCount(count)} ${count === 1 ? "buyer" : "buyers"}`;
// Buyers whose Sales Tax Status could not be asked for.
const notAskedFbr = (job) =>
  (job.noToken || 0) + (job.tokenRefused || 0) + (job.fbrFailed || 0);

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.stopped;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${style.className}`}
    >
      {style.label}
    </span>
  );
};

const Stat = ({ label, value, note }) => (
  <div role="group" aria-label={label}>
    <p className="text-slate-500 m-0">{label}</p>
    <p className="text-xl font-bold m-0">{value}</p>
    {note && <p className="text-xs text-slate-500 m-0">{note}</p>}
  </div>
);

// What a run did: the counts, then anything that kept a buyer from being checked.
const RunResult = ({ job }) => {
  const style = RESULT_STYLES[job.status] || RESULT_STYLES.stopped;
  const ended = formatDateTime(job.finishedAt || job.createdAt);
  const heading =
    job.status === "completed"
      ? `Last run completed on ${ended} in ${formatDuration(job.durationMs)}`
      : job.status === "stopped"
        ? `Last run was stopped on ${ended}, after ${formatCount(job.processed)} of ${buyers(job.total)}`
        : `Last run failed on ${ended}`;
  const leftOut = (job.walkIn || 0) + (job.noNumber || 0);

  return (
    <div className={`border rounded-lg p-4 ${style.box}`}>
      <p className={`font-semibold m-0 ${style.title}`}>
        {heading}
        {job.startedBy ? ` (started by ${job.startedBy})` : ""}
      </p>
      {job.error && (
        <p className="text-sm text-red-700 mt-1 mb-0" role="alert">
          {job.error}
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-3 text-sm">
        <Stat label="Buyers checked" value={formatCount(job.checked)} />
        <Stat
          label="Sales Tax Status changed"
          value={formatCount(job.salesTaxChanged)}
        />
        <Stat
          label="Registration Type changed"
          value={formatCount(job.registrationTypeChanged)}
        />
        <Stat label="ATL Status changed" value={formatCount(job.atlChanged)} />
        <Stat
          label="Left out"
          value={formatCount(leftOut)}
          note={`${formatCount(job.walkIn)} walk-in · ${formatCount(job.noNumber)} without NTN / CNIC`}
        />
      </div>

      {(notAskedFbr(job) > 0 || job.atlNotChecked > 0) && (
        <ul className="list-disc pl-5 space-y-1 text-sm mt-3 mb-0">
          {job.noToken > 0 && (
            <li>
              {buyers(job.noToken)} could not be checked with FBR: their account
              has no FBR token saved.
            </li>
          )}
          {job.tokenRefused > 0 && (
            <li>
              {buyers(job.tokenRefused)} could not be checked with FBR: FBR
              refused their account's token.
            </li>
          )}
          {job.fbrFailed > 0 && (
            <li>
              {job.fbrStopped
                ? `FBR stopped answering during this run, so ${buyers(job.fbrFailed)} could not be checked with it. Please run it again later.`
                : `FBR did not answer for ${buyers(job.fbrFailed)}.`}
            </li>
          )}
          {job.atlNotChecked > 0 && (
            <li>
              {job.atlListMissing
                ? "No Active Taxpayer List has been uploaded, so no ATL Status was updated."
                : `The ATL Status of ${buyers(job.atlNotChecked)} could not be read from the list.`}
            </li>
          )}
          <li>Those buyers keep the status they had.</li>
        </ul>
      )}

      {job.accounts?.length > 0 && (
        <div className="mt-3">
          <p className="font-semibold text-sm m-0 mb-1">
            Accounts whose buyers could not be checked with FBR
          </p>
          <div className="overflow-x-auto bg-white rounded-md border border-slate-200">
            <table
              className="w-full text-sm text-left"
              aria-label="Accounts not checked with FBR"
            >
              <thead>
                <tr className="text-slate-500 border-b">
                  <th className="py-2 px-3 font-semibold">Account</th>
                  <th className="py-2 px-3 font-semibold">Email</th>
                  <th className="py-2 px-3 font-semibold">Reason</th>
                  <th className="py-2 px-3 font-semibold">Buyers</th>
                </tr>
              </thead>
              <tbody>
                {job.accounts.map((account) => (
                  <tr key={account.userId} className="border-b last:border-0">
                    <td className="py-2 px-3">{account.userName || "-"}</td>
                    <td className="py-2 px-3 break-all">
                      {account.email || "-"}
                    </td>
                    <td className="py-2 px-3">
                      {ACCOUNT_REASONS[account.reason] || account.reason}
                    </td>
                    <td className="py-2 px-3">{formatCount(account.buyers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Re-checks every buyer's Sales Tax Status (with FBR) and ATL Status (against
// the uploaded list) and saves the latest. The work runs on the server; this
// starts it, follows it and shows how the last run went. Rendered only for an
// administrator (see Index.jsx): its requests answer 401 to anyone else.
const BuyerStatusJob = () => {
  const dispatch = useDispatch();
  const {
    hasLoadedBuyerStatusJob,
    buyerStatusJobError,
    activeBuyerStatusJob,
    lastBuyerStatusJob,
    buyerStatusJobHistory,
    buyerStatusAtlDataset,
    isStartingBuyerStatusJob,
    isStoppingBuyerStatusJob,
  } = useSelector((state) => state.ProgramSettingsReducer);

  useEffect(() => {
    dispatch(getBuyerStatusJob());
  }, [dispatch]);

  // While an update is running - started here or by another admin - keep the
  // progress fresh. Nothing is polled otherwise.
  const activeId = activeBuyerStatusJob?.id || "";
  useEffect(() => {
    if (!activeId) return undefined;
    const timer = setInterval(
      () => dispatch(getBuyerStatusJob()),
      POLL_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [dispatch, activeId]);

  const isRunning = Boolean(activeBuyerStatusJob);
  const isStopping =
    isStoppingBuyerStatusJob || Boolean(activeBuyerStatusJob?.stopRequested);

  const handleRun = () => {
    Swal.fire({
      title: "Update buyer statuses now?",
      text: "Every buyer that has an NTN or CNIC is checked with FBR and against the Active Taxpayer List. This can take several minutes and runs on the server.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, run it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3A80FB",
      cancelButtonColor: "#6c757d",
      customClass: { popup: "mui-swal-popup" },
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        if (container) container.style.zIndex = "2000";
      },
    }).then((result) => {
      if (result.isConfirmed) dispatch(runBuyerStatusJob(handleDangerVisible));
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3
          className="font-bold text-base m-0"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          Update Buyer Statuses
        </h3>
        <p className="text-sm text-slate-600 mt-1 mb-0">
          Checks every buyer again and saves the latest Sales Tax Status (from
          FBR) and ATL Status (from the Active Taxpayer List).
        </p>
      </div>

      {!hasLoadedBuyerStatusJob ? (
        buyerStatusJobError ? (
          <p className="text-sm text-red-600 m-0" role="alert">
            {buyerStatusJobError}
          </p>
        ) : (
          <div className="flex items-center h-12">
            <CircularProgress size={22} />
          </div>
        )
      ) : (
        <>
          {/* The update is running on the server */}
          {isRunning && (
            <div
              className="border border-blue-200 bg-blue-50 rounded-lg p-4"
              role="status"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="font-semibold text-blue-900 m-0">
                  {isStopping ? "Stopping..." : "Checking buyers..."}
                </p>
                <button
                  type="button"
                  disabled={isStopping}
                  onClick={() =>
                    dispatch(stopBuyerStatusJob(handleDangerVisible))
                  }
                  className="px-3 py-1 rounded-md border border-red-300 text-red-600 text-sm font-semibold bg-white disabled:opacity-50"
                >
                  {isStopping ? "Please Wait..." : "Stop"}
                </button>
              </div>
              <LinearProgress
                variant="determinate"
                value={activeBuyerStatusJob.percent || 0}
                sx={{ my: 1.5, height: 8, borderRadius: 4 }}
              />
              <p className="text-sm text-blue-900 m-0">
                {activeBuyerStatusJob.percent || 0}% ·{" "}
                {formatCount(activeBuyerStatusJob.processed)} of{" "}
                {buyers(activeBuyerStatusJob.total || 0)}
                {activeBuyerStatusJob.startedBy
                  ? ` · started by ${activeBuyerStatusJob.startedBy}`
                  : ""}
              </p>
              <p className="text-xs text-blue-800 mt-1 mb-0">
                This runs on the server - you can leave this page. Stopping
                keeps the buyers already checked.
              </p>
            </div>
          )}

          {!isRunning && lastBuyerStatusJob && (
            <RunResult job={lastBuyerStatusJob} />
          )}

          {!buyerStatusAtlDataset ? (
            <div className="border border-amber-200 bg-amber-50 text-amber-900 rounded-lg p-3 text-sm">
              No Active Taxpayer List has been uploaded yet, so the ATL Status
              will not be updated. Upload the list under Active Taxpayers
              first.
            </div>
          ) : (
            <p className="text-sm text-slate-600 m-0">
              ATL Status is read from the list uploaded on{" "}
              <span className="font-semibold">
                {formatDateTime(buyerStatusAtlDataset.importedAt)}
              </span>{" "}
              ({formatCount(buyerStatusAtlDataset.totalRows)} active taxpayers).
            </p>
          )}

          <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-4">
            <h6 className="font-semibold text-sm mb-2">How this works</h6>
            <ul className="list-disc pl-5 space-y-1 text-sm mb-0">
              <li>
                Walk-in customers and buyers without an NTN or CNIC are left
                out
              </li>
              <li>
                FBR is asked about a buyer with the token of the company of the
                account that buyer belongs to - never the general token
              </li>
              <li>
                The Registration Type follows FBR: Registered when FBR lists
                the buyer as registered, Unregistered when it does not
              </li>
              <li>
                If a buyer cannot be checked, it keeps the status and
                Registration Type it had
              </li>
            </ul>
          </div>

          <button
            type="button"
            disabled={isRunning || isStartingBuyerStatusJob}
            onClick={handleRun}
            className="w-full md:w-1/3 block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
            style={{ background: globalStyle.buttonColor }}
          >
            {isRunning
              ? "Running..."
              : isStartingBuyerStatusJob
                ? "Starting..."
                : "Run Now"}
          </button>

          {/* History */}
          <div>
            <h4
              className="font-bold text-sm m-0 mb-2"
              style={{
                fontFamily: globalStyle.fontFamilyHeading,
                color: globalStyle.curveDarkColor,
              }}
            >
              Recent Runs
            </h4>
            {!buyerStatusJobHistory.length ? (
              <p className="text-sm text-slate-500 m-0">Not run yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm text-left"
                  aria-label="Recent runs"
                >
                  <thead>
                    <tr className="text-slate-500 border-b">
                      <th className="py-2 pr-4 font-semibold">Date</th>
                      <th className="py-2 pr-4 font-semibold">Started By</th>
                      <th className="py-2 pr-4 font-semibold">Status</th>
                      <th className="py-2 pr-4 font-semibold">Checked</th>
                      <th className="py-2 pr-4 font-semibold">
                        Sales Tax Changed
                      </th>
                      <th className="py-2 pr-4 font-semibold">
                        Type Changed
                      </th>
                      <th className="py-2 pr-4 font-semibold">ATL Changed</th>
                      <th className="py-2 pr-4 font-semibold">
                        Not Checked With FBR
                      </th>
                      <th className="py-2 pr-4 font-semibold">Time Taken</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buyerStatusJobHistory.map((job) => (
                      <tr
                        key={job.id}
                        className="border-b last:border-0 align-top"
                      >
                        <td className="py-2 pr-4 whitespace-nowrap">
                          {formatDateTime(job.createdAt)}
                        </td>
                        <td className="py-2 pr-4">{job.startedBy || "-"}</td>
                        <td className="py-2 pr-4">
                          <StatusBadge status={job.status} />
                        </td>
                        <td className="py-2 pr-4">{formatCount(job.checked)}</td>
                        <td className="py-2 pr-4">
                          {formatCount(job.salesTaxChanged)}
                        </td>
                        <td className="py-2 pr-4">
                          {formatCount(job.registrationTypeChanged)}
                        </td>
                        <td className="py-2 pr-4">
                          {formatCount(job.atlChanged)}
                        </td>
                        <td className="py-2 pr-4">
                          {formatCount(notAskedFbr(job))}
                        </td>
                        <td className="py-2 pr-4 whitespace-nowrap">
                          {formatDuration(job.durationMs)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BuyerStatusJob;
